import { JobSectionVerb } from '../../../modules/job.section.rights';
import { PortalClientSection, PortalClientSectionRightIds } from '../../../modules/portal.client.section.rights';
import { SecurityRoleTiny } from '../../../security.role';
import { SecurityRoleUtility } from '../../../utility/security.roles.utility';
import { SectionRights } from './job.section.policy';

/** What a client role may do on each Client Portal job and site tab. */
export type PortalClientSectionRights = Record<PortalClientSection, SectionRights>;

/**
 * The one reader of the Client Portal tab rights; the portal's tab plans and the portal server both decide from it.
 *
 * Each verb is read on its own and never stands in for View. Deleting an activity follows Edit, as it did under
 * Create & Edit Activities: the tab offers no Delete right. A role with no portal right, a System Administrator
 * included, gets nothing.
 */
export function getPortalClientSectionRights(role: SecurityRoleTiny | null | undefined): PortalClientSectionRights {

    const has = (right: number | undefined) => !!role && right !== undefined && SecurityRoleUtility.checkTinyRights(role, right);
    const section = (key: PortalClientSection): SectionRights => {

        const ids = PortalClientSectionRightIds[key];
        const view = has(ids.view);
        const edit = view && has(ids.edit);

        return {
            view,
            create: view && has(ids.create),
            edit,
            delete: key === 'jobActivities' || key === 'siteActivities' ? edit : view && has(ids.delete),
        };
    };

    return Object.fromEntries(Object.keys(PortalClientSectionRightIds).map(key => [key, section(key as PortalClientSection)])) as PortalClientSectionRights;
}

/** The refusal for a client tab action, or null when the role allows it; a missing View is named before the verb. */
export function portalClientSectionRefusal(rights: PortalClientSectionRights, section: PortalClientSection, verb: JobSectionVerb): string | null {

    const names: Record<PortalClientSection, string> = {
        jobTasks: 'job tasks',
        jobSors: 'job SORs',
        jobItems: 'job items',
        jobAppointments: 'job appointments',
        jobInvoices: 'job invoices',
        jobFiles: 'job files',
        jobActivities: 'job activities',
        siteContacts: 'site contacts',
        siteServices: 'site services',
        siteQuotes: 'site quotes',
        siteJobs: 'site jobs',
        siteInvoices: 'site invoices',
        siteFiles: 'site files',
        siteActivities: 'site activities',
    };
    const verbs: Record<JobSectionVerb, string> = { view: 'view', create: 'add', edit: 'edit', delete: 'delete' };
    const missing = rights[section].view ? verb : 'view';

    return rights[section][verb] ? null : `You don't have permission to ${verbs[missing]} ${names[section]}.`;
}
